<template>
  <div class="webviewer-wrapper fill-height"
       v-resize="debounceOnResize">
    <div v-if="docLoaded && meta?.pageCount"
         class="d-flex justify-space-between">
      <v-progress-linear height="32"
                         color="light-blue"
                         :value="progressValue">
        <template v-slot:default="{value}">
          Page {{ meta.currentPage }} / {{ meta.pageCount }}
        </template>
      </v-progress-linear>
    </div>
    <div id="pdf_viewer"
         class="fill-height">
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import WebViewer from '@pdftron/webviewer'
import {debounce, find, isInteger, isObject, merge} from 'lodash';
import pdftron from "../../../../../pdftron/pdftron";
import bugsnagService from '../../../services/bugsnagService';
import presentationAnnotationService from '../../../services/presentationAnnotationService';
import moment from 'moment-timezone';
import contentService from '../../../services/contentService';

export default {
  name: 'view-pdf-presentation',
  props: {
    presentation: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    slides: {
      type: Array,
      required: false,
      default: [],
    },
    slideNotes: {
      type: Array,
      required: false,
      default: [],
    },
  },
  data() {
    return {
      busy: false,
      instance: null,
      docLoaded: false,
      //sessionInterval: null,
      annotationsVisible: false,
    }
  },
  computed: {
    ...mapGetters([
      'authenticated',
      'canUsePresentationNotes', // MLD-6435
      'isAdmin',
      'isEmployee',
      'isMember',
      'isSuperAdmin',
      'team',
      'user',
      'users',
    ]),
    ...mapGetters('content', [
      'activity',
      'activityID',
      'isFullScreen', // MLD-6890
    ]),
    ...mapGetters('meta', [
      'meta',
    ]),
    annotationManager() {
      return this.instance ? this.instance.Core.annotationManager : null;
    },
    documentViewer() {
      return this.instance ? this.instance.Core.documentViewer : null;
    },
    file() {
      if (!this.presentation) {
        return null;
      }
      // MLD-3075
      /*if (this.team && this.team.xod_enabled && (this.presentation.type_pdf || this.presentation.converts_to_pdf)) {
        const xodFile = find(this.presentation.files, file => file.path === 'presentation_optimized.xod');
        if (xodFile) {
          return xodFile;
        }
      }*/
      return find(this.presentation.files, file => file.path === this.presentation.main_path);
    },
    canBeAnnotated() { // MLD-5717
      return this.team
        && this.team.presentation_annotations_enabled
        && this.presentation
        && !!this.presentation.settings.can_be_annotated;
    },
    canBeDownloaded() {
      return this.presentation && !!this.presentation.settings.can_be_exported;
    },
    canBePrinted() {
      return this.presentation && !!this.presentation.settings.can_be_printed_by_user;
    },
    progressValue() {
      if (this.docLoaded
        && this.meta.currentPage
        && this.meta.pageCount) {
        return parseInt((this.meta.currentPage / this.meta.pageCount) * 100);
      }
      return null;
    },
    iframeDocument() {
      const iframe = document.querySelector('#pdf_viewer > iframe');
      if (iframe) {
        return iframe.contentDocument || iframe.contentWindow.document;
      }
      return null;
    },
  },
  async beforeDestroy() {
    // MLD-7714 Cancel any pending debounced calls when component is destroyed
    if (this.debouncedOnResize) {
      this.debouncedOnResize.cancel()
    }

    this.removeFullscreenListener();
    window.removeEventListener('message', this.handleMessage);
    try {
      if (this.instance) {
        // await this.instance.UI.closeDocument()
        // this.instance.UI.dispose();
        this.instance = null;
      }
    } catch (e) {
      console.log('silently caught exception in beforeDestroy()', e.message);
    }
  },
  async created() {
    this.busy = true;
    this.debounceOnResize = debounce(this.onResize, 100); // MLD-7714
    await this.fetchData();
  },
  watch: {
    presentation(val, oldVal) {
      if (val && oldVal && val !== oldVal) {
        console.log(`Changed from presentation ${oldVal.id} to ${val.id}`);
        if (this.instance && this.file) {
          if (this.documentViewer) {
            this.documentViewer.dispose();
          }
          this.instance.loadDocument(this.file.url);
        }
      }
    }
  },
  mounted() {
    window.addEventListener('message', this.handleMessage); // MLD-6435
    this.configureWebviewerHeight();
    this.loadViewer();
  },
  methods: {
    onResize() { // MLD-7714
      console.log('[PDF] webviewer-wrapper resized:')
      this.configureWebviewerHeight();
    },
    configureWebviewerHeight() { // MLD-7714
      console.log('[PDF] configureWebviewerHeight...');
      const webviewWrapper = document.querySelector('.webviewer-wrapper');
      if (!webviewWrapper) {
        console.log('[PDF] There was a problem finding the webviewWrapper element.');
        return;
      }
      const pdfViewer = document.getElementById('pdf_viewer');
      if (!pdfViewer) {
        console.log('[PDF] There was a problem finding the pdfViewer element.');
        return;
      }
      const windowHeight = window.innerHeight;
      const webviewWrapperTop = webviewWrapper.getBoundingClientRect().top;
      const height = `${windowHeight - webviewWrapperTop}px`;
      webviewWrapper.style.height = height;
      webviewWrapper.style.overflow = 'hidden';
      pdfViewer.style.height = height;
      pdfViewer.style.overflow = 'hidden';
      console.log('[PDF] configureWebviewerHeight... done');
    },
    async fetchData() { // MLD-6435

    },
    handleMessage(event) { // MLD-6435
      if (event.origin !== window.location.origin) {
        return; // Ignore messages from different origins
      }
      const data = event.data;
      if (isObject(data) && data.type === 'gotoPage' && isInteger(data.page)) {
        this.setCurrentPage(data.page);
      }
    },
    setCurrentPage(page) {
      this.documentViewer.setCurrentPage(page, true);
    },
    async loadViewer() { // MLD-7714
      const viewerElement = document.getElementById('pdf_viewer');
      if (!viewerElement) {
        console.log('[PDF] There was a problem finding the pdf_viewer element.');
        return;
      }
      if (!this.presentation) {
        console.log('[PDF] There was a problem finding the presentation.');
        return;
      }
      if (this.presentation.type_pdf || this.presentation.converts_to_pdf) {
        const url = this.file.url;
        try {
          const options = pdftron.buildConfigForPresentation(this.presentation, url);
          const finalConfig = pdftron.buildFinalConfig(url, options, false);
          const instance = this.instance = await WebViewer(finalConfig, viewerElement);
          if (finalConfig.fullAPI) {
            await pdftron.initializeFullAPI(finalConfig, this.instance);
          }
          try {
            await pdftron.configureUI(instance, this.presentation, false, null, null, null);
          } catch (error) {
            console.log('[PDF] There was a problem configuring the UI.', error.message);
          }

          const {Core} = instance;
          const {documentViewer, Tools} = Core;

          const DocumentViewerEvents = instance.Core.DocumentViewer.Events;
          const UIEvents = instance.UI.Events;

          // @see https://docs.apryse.com/web/guides/events/loading-events?language=javascript+%28sdk+v8.0%2B%29#document-load-error-handling
          // @see https://docs.apryse.com/web/guides/events/ui-events#document-load-error-event
          instance.UI.addEventListener(UIEvents.LOAD_ERROR, async (error) => {
            console.error(`[PDF] There was a problem loading the PDF file.`, error.detail.message);
            await this.displayLoadingError(instance, error);
          });

          // @see https://docs.apryse.com/web/guides/events/ui-events#toolbar-events
          instance.UI.addEventListener(UIEvents.TOOLBAR_GROUP_CHANGED, async (event) => {
            console.log('[PDF] toolbar group changed', event.detail);
          });

          // @see https://sdk.apryse.com/api/web/Core.DocumentViewer.html#event:displayModeUpdated__anchor
          documentViewer.addEventListener(DocumentViewerEvents.DISPLAY_MODE_UPDATED, async () => {
            //const displayModeManager = documentViewer.getDisplayModeManager();
            //const displayMode = displayModeManager.getDisplayMode();
            console.log('[PDF] display mode updated');
          });

          documentViewer.addEventListener(DocumentViewerEvents.FIT_MODE_UPDATED, async () => {
            //const fitMode = documentViewer.getFitMode();
            console.log('[PDF] fit mode updated');
          });

          documentViewer.addEventListener(DocumentViewerEvents.TOOL_MODE_UPDATED, async (newToolObject, oldToolObject) => {
            console.log('[PDF] tool mode updated');
          });

          documentViewer.addEventListener(DocumentViewerEvents.TOOL_UPDATED, async (toolModeClass) => {
            console.log('[PDF] tool updated', toolModeClass);
          });

          documentViewer.addEventListener(DocumentViewerEvents.DOCUMENT_LOADED, async () => {
            console.log('[PDF] document loaded...');

            console.log('[PDF] setting tool mode to PAN...')
            documentViewer.setToolMode(documentViewer.getTool(Tools.ToolNames.PAN));
            console.log('[PDF] setting tool mode to PAN... done')

            // @see https://docs.apryse.com/web/guides/documentviewer/adjusting-layout-zoom
            console.log('[PDF] setting display mode to SINGLE...');
            const displayModeManager = documentViewer.getDisplayModeManager();
            displayModeManager.disableVirtualDisplayMode();
            displayModeManager.setDisplayMode(new instance.Core.DisplayMode(documentViewer, instance.Core.DisplayModes.Single));
            console.log('[PDF] setting display mode to SINGLE... done');

            console.log('[PDF] setting fit mode to FIT_PAGE...');
            // instance.UI.setFitMode(instance.UI.FitMode.FitPage);
            documentViewer.setFitMode(documentViewer.FitMode.FitPage);
            console.log('[PDF] setting fit mode to FIT_PAGE... done');

            console.log('[PDF] enable arrow key navigation...');
            documentViewer.enableArrowKeyNavigation();
            console.log('[PDF] enable arrow key navigation... done');

            console.log('[PDF] disable automatic linking...');
            documentViewer.disableAutomaticLinking();
            console.log('[PDF] disable automatic linking... done');

            console.log('[PDF] enable read only mode...');
            documentViewer.enableReadOnlyMode();
            console.log('[PDF] enable read only mode... done');

            // MLD-8021 Fix the Acertis and Bracco bugs
            // @see https://support.apryse.com/support/tickets/128111
            const disableWidgetHighlighting = this.presentation.settings?.pdf_disable_widget_highlighting ?? false;
            if (disableWidgetHighlighting) {
              try {
                console.log('[PDF] Disabling widget highlighting...');
                instance.Core.annotationManager.getFieldManager().disableWidgetHighlighting();
                console.log('[PDF] Disabling widget highlighting... done');
              } catch (error) {
                console.log('[PDF] Error disabling widget highlighting:', error.message);
              }
            }

            /*try {
              console.log('[PDF] setting instance...');
              this.instance = instance;
              console.log('[PDF] setting instance... done');
            } catch (e) {
              console.error('[PDF] There was a problem creating the instance.', e);
            }*/

            // call methods relating to the loaded document
            const meta = {
              presentationID: this.presentation.id,
              currentPage: this.documentViewer.getCurrentPage(),
              pageCount: this.documentViewer.getPageCount(),
            }
            await this.$store.dispatch('meta/loadDocument', meta);
            this.docLoaded = true;
            this.pageChanged(this.meta.currentPage); // MLD-4832

            console.log('[PDF] document loaded... done');
          });

          documentViewer.addEventListener(DocumentViewerEvents.DOCUMENT_UNLOADED, async () => {
            console.log('[PDF] document unloaded');
          });

          /*documentViewer.addEventListener(DocumentViewerEvents.PAGE_COMPLETE, async (pageNumber, canvas) => {
              console.log(`PDF page ${pageNumber} complete`, DocumentViewerEvents.PAGE_COMPLETE);
            });*/

          documentViewer.addEventListener(DocumentViewerEvents.PAGE_NUMBER_UPDATED, async () => {
            const pageNumber = documentViewer.getCurrentPage();
            const pageCount = documentViewer.getPageCount();
            console.log(`[PDF] page number updated: ${pageNumber}/${pageCount}`);
            this.pageChanged(pageNumber)
          });
        } catch (error) {
          console.error('[PDF] There was a problem creating the WebViewer instance.', error.message);
        }
      }
    },
    async displayLoadingError(instance, err) { // MLD-7714
      bugsnagService.notify(err); // MLD-5144

      // @see https://docs.apryse.com/api/web/UI.html#.closeElements
      instance.UI.closeElements(['errorModal', 'loadingModal', 'progressModal']);

      // @see https://docs.apryse.com/api/web/UI.html#.showWarningMessage__anchor
      const message = `There was a problem loading the PDF. Click the browser reload button, or press RELOAD. Press the X to cancel.`;
      instance.UI.showWarningMessage({
        title: `PDF Loading Error - Presentation ${this.presentation?.id}`,
        message: message,
        confirmBtnText: 'RELOAD',
        onConfirm: () => {
          console.log('The user clicked the confirm button');
          window.location.reload();
          return Promise.resolve();
        },
        onCancel: () => {
          console.log('The user clicked the cancel button');
          this.$router.push({name: `${this.$root.routePrefix}.home`});
          return Promise.resolve();
        }
      })
    },
    fitPageToScreen() { // MLD-6890
      try {
        this.instance?.UI?.setFitMode(this.instance.UI.FitMode.FitPage);
        //console.log('setFitMode', this.instance.UI.FitMode.FitPage);
      } catch (e) {
      }
    },
    fullscreenChangeListener(event) { // MLD-6890
      const isFullScreen = !!this.iframeDocument?.fullscreenElement;
      this.$store.dispatch('content/setIsFullScreen', isFullScreen);
      this.fitPageToScreen();

      this.logFullScreenEvent(isFullScreen);
      /*if (isFullScreen) {
        this.enterFullScreen();
      } else {
        this.exitFullScreen();
      }*/
    },
    logFullScreenEvent(isFullScreen) {
      const form = {
        session_id: this.session.uuid,
        session_started_at: moment(this.session.started_at).valueOf(),
        presentation_id: this.session.presentation_id,
        elapsed_ms: this.meta.elapsedMS,
        category: 'fullscreen',
        action: 'change',
        uri: `${isFullScreen ? 'enter' : 'exit'}`,
      }
      contentService.logEvent(form)
        .catch(err => {
        });
    },
    removeFullscreenListener() { // MLD-6890
      this.iframeDocument?.removeEventListener('fullscreenchange', this.fullscreenChangeListener); // MLD-6890
    },
    pageChanged(pageNumber) { // MLD-4832
      //console.log('changePage', pageNumber);
      this.$emit('change_page', pageNumber);
    },
  }
}
</script>
